/** @jsx jsx */
// @ts-check
import {
  ArrowLeftIcon,
  // ArrowRightIcon,
  Box,
  Flex,
  Link,
  Text,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import { resolve } from '../helpers/path';
import BrandLogo from './Brand/BrandLogo';
import CoverImage from './CoverImage';

/**
 * A carousel using only CSS.
 * @see https://css-tricks.com/css-only-carousel/ for the implementation.
 * It displays two exhibitors per slide, as in the design.
 * @typedef {object} Brand
 * @property {string} brandId
 *
 * @param {object} props The props.
 * @param {Brand[]} props.brandChunks An array of two brands.
 * @param {string} props.basePath The path to create the brand paths from.
 * @param {boolean} props.showIndicators Should indicators below the carousel be showing?
 * @param {string} props.className The class name for styling the carousel.
 */
export default function BrandCarousel({
  brandChunks,
  basePath,
  showIndicators,
  className,
  ...rest
}) {
  const lastIndex = brandChunks.length - 1;
  return (
    <Box {...rest}>
      <Flex
        // The carousel viewport.
        as="ol"
        className={className}
        sx={{
          display: 'flex',
          listStyle: 'none',
          margin: 0,
          padding: 0,
          // Add smooth scrolling.
          overflowX: 'scroll',
          scrollBehavior: 'smooth',
          scrollSnapType: 'x mandatory',
          // Hide the scroll bar.
          scrollbarColor: 'transparent transparent',
          scrollbarWidth: 0,
        }}
      >
        {brandChunks?.map((brands, index) => (
          <DualSlide
            // A slide containing two exhibitors.
            key={brands?.[0]?.brandId || index}
            brands={brands}
            basePath={basePath}
            as="li"
            id={`slide${index + 1}`}
            previousId={`slide${index === 0 ? lastIndex + 1 : index}`}
            nextId={`slide${index === lastIndex ? 1 : index + 2}`}
            sx={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              // Ensure 100% width per slide.
              flex: ['0 0 95%', '0 0 90%'],
              // Snap to the slides when scrolling.
              scrollSnapAlign: 'center',
            }}
          />
        ))}
      </Flex>
      {showIndicators && (
        <Flex justify="center">
          {brandChunks?.map((brands, index) => (
            <Indicator
              key={brands?.[0]?.brandId || index}
              id={`slide${index + 1}`}
              brands={brands}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
}

function Indicator({ id, brands }) {
  return (
    <Link
      href={`#${id}`}
      sx={{
        borderRadius: 'round',
        fontSize: 0,
        paddingX: 4,
        paddingY: 1,
        marginY: 3,
        marginX: 1,
        backgroundColor: 'primary',
        transition: 'backgroundColor',
        ':hover': { backgroundColor: 'secondary' },
      }}
    >
      {brands.map(({ name }) => name).join(', ')}
    </Link>
  );
}

/** 
  A single slide displaying two brands.
 */
function DualSlide({ brands, basePath, id, nextId, previousId, ...rest }) {
  return (
    <Box
      id={id}
      sx={{
        position: 'relative',
        // Reverse the first item to display the name first.
        // We can adapt this for mobile too because it's only done via CSS.
        '>:nth-of-type(odd)': { flexDirection: 'row-reverse' },
        // Display the arrows on hover only.
        ':hover>.bottlebooks-carousel-arrow': {
          transform: 'translate(0,-50%)',
          opacity: 1,
        },
      }}
      {...rest}
    >
      {brands?.map(brand => (
        <FeaturedBrand
          key={brand.brandId}
          brand={brand}
          to={resolve([basePath, brand.slug])}
          sx={{ height: `${100 / brands.length}%` }}
        />
      ))}
      {previousId && (
        <NavigationButton
          // This button is transparent, but executes the actual action.
          direction="previous"
          href={`#${previousId}`}
          sx={{
            // Display the arrow on hover only.
            opacity: 0,
            transform: 'translate(-16px, -50%)',
          }}
          className="bottlebooks-carousel-arrow"
        />
      )}
      {nextId && (
        <NavigationButton
          // This button is transparent, but executes the actual action.
          direction="next"
          href={`#${nextId}`}
          sx={{
            // Display the arrow on hover only.
            opacity: 0,
            transform: 'translate(16px, -50%)',
          }}
          className="bottlebooks-carousel-arrow"
        />
      )}
    </Box>
  );
}

/**
 * A button to go to the next/previous slide.
 * @param {object} props The props.
 * @param {'previous'|'next'} props.direction The direction.
 * @param {string} props.href The link to the slide.
 * @param {object} props.sx The styles (transformed by jsx into className).
 * @param {string} props.className The class name for styling.
 */
function NavigationButton({ direction = 'previous', ...rest }) {
  return (
    <Link
      // @ts-ignore
      sx={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 'raised',
        marginX: 3,
        width: 90,
        height: 90,
        lineHeight: '90px', // Centers the icon vertically.
        textAlign: 'center',
        [direction === 'next' ? 'right' : 'left']: 0,
        borderRadius: 'round',
        boxShadow: 'floating',
        color: 'lightestText',
        backgroundColor: 'nuanced',
        transition: theme =>
          `${theme.transition.opacity}, ${theme.transition.transform}, ${theme.transition.color}`,
      }}
      {...rest}
    >
      {direction === 'previous' ? (
        <ArrowLeftIcon size="medium" sx={{ color: 'currentColor' }} />
      ) : (
        <ArrowLeftIcon
          size="medium"
          sx={{ color: 'currentColor', transform: 'rotate(180deg)' }}
        />
      )}
    </Link>
  );
}

/**
 * A single brand displayed with text and image side by side.
 */
function FeaturedBrand({ brand, to, ...rest }) {
  const { mainImage, name, countryName, region } = brand;
  return (
    <Flex
      as={Link}
      to={to}
      align="stretch"
      direction="row"
      sx={{
        color: 'text',
        // Hover effects:
        ':hover': {
          color: 'lightText',
          h4: { color: 'secondary' },
          '.bottlebooks-coverImage img': { transform: 'scale(1.05)' },
        },
      }}
      {...rest}
    >
      <Box
        sx={{ backgroundColor: 'nuanced', position: 'relative' }}
        // Fall back to 50% width if calc isn't supported.
        css={{ width: ['50%', 'calc(50% + 20px)'] }}
      >
        {mainImage && (
          <CoverImage
            {...mainImage}
            alt={name}
            imgStyle={{ transition: null }}
            sx={{
              img: {
                transition: theme =>
                  `${theme.transition.transform}, ${theme.transition.opacity}`,
              },
            }}
            className="bottlebooks-coverImage"
          />
        )}
      </Box>
      <Flex direction="column" align="stretch" sx={{ zIndex: 'raised' }}>
        <Box sx={{ backgroundColor: 'white', flex: '1', marginX: '-20px' }} />
        <Box
          sx={{
            marginX: '-20px',
            border: '20px solid',
            borderColor: 'white',
            borderLeftColor: 'transparent',
            borderRightColor: 'transparent',
          }}
        />
        <Box sx={{ backgroundColor: 'white', flex: '1', marginX: '-20px' }} />
      </Flex>
      <Flex
        direction="column"
        align="center"
        justify="center"
        sx={{ paddingX: 3, paddingY: 4, textAlign: 'center', zIndex: 'raised' }}
        // Fall back to 50% width if calc isn't supported.
        css={{ width: ['50%', 'calc(50% - 20px)'] }}
      >
        <BrandLogo size="default" brand={brand} />
        <Title
          variant="small"
          sx={{
            marginTop: 3,
            marginBottom: 1,
            color: 'primary',
            overflow: 'visible',
          }}
        >
          {name}
        </Title>
        <Text variant="small">
          {[countryName, region].filter(Boolean).join(', ')}
        </Text>
      </Flex>
    </Flex>
  );
}

export const fragment = graphql`
  fragment BrandCarousel on AbstractBrand {
    brandId
    slug
    name
    countryName: country(full: true)
    region
    ...BrandLogoDefault
    mainImage {
      fluid(maxWidth: 660, maxHeight: 300, crop: FILL, gravity: FACES_AUTO) {
        src
        srcSet
        sizes
        base64
        aspectRatio
      }
    }
  }
`;
